import React, { useEffect, useRef, useState } from "react";
import {
  Users,
  Monitor,
  HandHeart,
  Calendar,
  ShieldCheck,
  Lock,
  Mail,
  ArrowRight,
  ExternalLink,
  UserPlus,
  MessageCircle,
  BookOpen,
  Wallet,
  Home as HomeIcon,
  Sparkles,
  Globe,
} from "lucide-react";
import { api } from "../lib/auth";

const LOGO_URL = "/img/btc-logo-pink.png";
const CHARITY_NUMBER = "1183619";

const ONCE_URL = "https://buy.stripe.com/dRm00l7Rh2V51pU5xL9fW00";
const MONTHLY_URL = "https://buy.stripe.com/dRm4gB4F58fp5Gagcp9fW01";

const externalProps = {
  target: "_blank",
  rel: "noopener noreferrer",
};

// -------- Campaign: 5,000 Strong --------
const CAMPAIGN_SERVICES = [
  {
    icon: UserPlus,
    title: "Virtual Buddy Matching",
    body: "Every member is paired with a buddy who understands the journey, so no one ever faces cancer alone.",
  },
  {
    icon: MessageCircle,
    title: "Peer Support Community",
    body: "Safe, moderated spaces to connect with others walking the same road.",
  },
  {
    icon: BookOpen,
    title: "Practical Resource Library",
    body: "Guides, checklists, and tools for treatment, recovery, and daily life with cancer.",
  },
  {
    icon: Wallet,
    title: "Financial Assistance Guidance",
    body: "Help finding and applying for grants, aid, and support programs.",
  },
  {
    icon: HomeIcon,
    title: "Caregiver and Family Support",
    body: "Dedicated resources for the people standing beside the patient.",
  },
  {
    icon: Sparkles,
    title: "Survivor Pathways",
    body: "Support that continues after treatment ends, because the journey does not stop at remission.",
  },
  {
    icon: Globe,
    title: "Community Outreach and Events",
    body: "Awareness programs and gatherings that strengthen local support networks.",
  },
];

function useCampaignProgress(pollMs = 60000) {
  const [state, setState] = useState({ donors: 0, goal: 5000, ready: false });
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/campaign/progress");
        if (!cancelled) {
          setState({
            donors: Math.max(0, Number(data.donors) || 0),
            goal: Math.max(1, Number(data.goal) || 5000),
            ready: true,
          });
        }
      } catch (_e) {
        // On failure, keep last-known value. Never show zero due to transient errors.
        if (!cancelled) setState((s) => ({ ...s, ready: true }));
      }
    };
    tick();
    timer.current = setInterval(tick, pollMs);
    return () => {
      cancelled = true;
      if (timer.current) clearInterval(timer.current);
    };
  }, [pollMs]);

  return state;
}

function AnimatedCount({ value, durationMs = 1400 }) {
  const [display, setDisplay] = useState(0);
  const from = useRef(0);
  const startedAt = useRef(0);
  const rafId = useRef(0);

  useEffect(() => {
    cancelAnimationFrame(rafId.current);
    from.current = display;
    startedAt.current = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - startedAt.current) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      const next = Math.round(from.current + (value - from.current) * eased);
      setDisplay(next);
      if (p < 1) rafId.current = requestAnimationFrame(step);
    };
    rafId.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, durationMs]);

  return <span>{display.toLocaleString("en-GB")}</span>;
}

export default function Donate() {
  const progress = useCampaignProgress(60000);
  const pct = Math.min(100, Math.round((progress.donors / progress.goal) * 1000) / 10);

  const scrollToTracker = () => {
    document.getElementById("live-tracker")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="btc-donate min-h-screen text-[#0B132B]">
      {/* -------- HERO -------- */}
      <header
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, #FFF3F7 0%, #FDE3EE 45%, #FBD3E3 100%)",
        }}
      >
        {/* Soft radial glow accents */}
        <div
          aria-hidden="true"
          className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full opacity-40"
          style={{ background: "radial-gradient(closest-side, #FBBFD8, transparent 70%)" }}
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-40 -right-16 w-[600px] h-[600px] rounded-full opacity-40"
          style={{ background: "radial-gradient(closest-side, #F7B2CD, transparent 70%)" }}
        />

        <div className="relative max-w-[1180px] mx-auto px-5 sm:px-8 lg:px-12 pt-14 pb-16 lg:pt-20 lg:pb-24 text-center">
          <img
            src={LOGO_URL}
            alt="Bonded Thru Cancer — The Buddy System"
            className="mx-auto h-40 sm:h-52 md:h-64 lg:h-72 w-auto select-none"
            draggable={false}
          />

          <h1
            data-testid="donate-headline"
            className="mt-6 lg:mt-8 font-extrabold tracking-tight text-[#111827] leading-[1.02] text-[44px] sm:text-6xl lg:text-[76px]"
            style={{ fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif" }}
          >
            <span className="block">Fuel the Fight.</span>
            <span className="block">Power the Platform.</span>
          </h1>

          <p className="mt-6 text-[17px] sm:text-lg lg:text-xl text-[#1F2937] max-w-3xl mx-auto leading-relaxed">
            Your gift does two things at once. It brings comfort and practical support to families
            facing cancer, and it keeps the first platform of its kind alive, a place where patients,
            survivors, and families connect, share, and heal together.
          </p>

          {/* Primary donation actions */}
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center">
            <a
              href={ONCE_URL}
              {...externalProps}
              data-testid="donate-hero-once"
              className="btc-donate-btn inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white font-semibold text-base shadow-[0_10px_30px_-8px_rgba(232,70,141,0.55)] hover:shadow-[0_14px_36px_-6px_rgba(232,70,141,0.7)] transition-all"
            >
              Donate Once
              <ExternalLink className="w-4 h-4 opacity-90" />
            </a>
            <a
              href={MONTHLY_URL}
              {...externalProps}
              data-testid="donate-hero-monthly"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white text-[#D6316E] font-semibold text-base border-2 border-[#F7B2CD] hover:border-[#E8468D] hover:bg-[#FFF3F7] transition-all"
            >
              Give Monthly
              <ExternalLink className="w-4 h-4 opacity-90" />
            </a>
          </div>
        </div>
      </header>

      {/* -------- MISSION SECTION -------- */}
      <section className="bg-white">
        <div className="max-w-[1180px] mx-auto px-5 sm:px-8 lg:px-12 py-14 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-7">
            <MissionCard
              icon={Users}
              title="For the Families"
              body="Direct support for cancer patients, survivors, and the people who love them. Comfort, connection, and practical help when it matters most."
            />
            <MissionCard
              icon={Monitor}
              title="For the Portal"
              body="Every donation keeps this platform free for every patient and family who needs it. No paywalls. No barriers. Just a community that shows up."
            />
          </div>
        </div>
      </section>

      {/* -------- DONATION OPTIONS -------- */}
      <section id="choose-how" className="bg-white scroll-mt-8">
        <div className="max-w-[1180px] mx-auto px-5 sm:px-8 lg:px-12 pb-14 lg:pb-20">
          <h2
            className="text-center font-extrabold tracking-tight text-[#111827] text-3xl sm:text-4xl lg:text-5xl mb-10 lg:mb-12"
          >
            Choose How You Give
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-7 max-w-[900px] mx-auto">
            {/* Donate Once */}
            <article
              data-testid="donate-card-once"
              className="bg-white border border-[#F1D9E5] rounded-3xl p-7 lg:p-9 text-center shadow-[0_2px_20px_-8px_rgba(232,70,141,0.15)] hover:shadow-[0_10px_36px_-10px_rgba(232,70,141,0.28)] transition-shadow"
            >
              <div className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ background: "linear-gradient(135deg, #F7B2CD 0%, #E8468D 100%)" }}>
                <HandHeart className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <h3 className="font-extrabold text-2xl lg:text-3xl text-[#111827] mb-3">Donate Once</h3>
              <p className="text-[15px] leading-relaxed text-[#4B5563] mb-7">
                Give any amount from £5, one time. Every pound fuels the mission on both fronts.
              </p>
              <a
                href={ONCE_URL}
                {...externalProps}
                data-testid="donate-card-once-cta"
                className="btc-donate-btn inline-flex w-full items-center justify-center gap-2 px-6 py-3.5 rounded-full text-white font-semibold text-base shadow-[0_8px_20px_-6px_rgba(232,70,141,0.5)] hover:shadow-[0_12px_28px_-6px_rgba(232,70,141,0.65)] transition-all"
              >
                Donate Once
                <ExternalLink className="w-4 h-4" />
              </a>
              <p className="mt-3 text-xs text-[#6B7280]">Opens in a new tab</p>
            </article>

            {/* Give Monthly */}
            <article
              data-testid="donate-card-monthly"
              className="relative bg-white border-2 border-[#F7B2CD] rounded-3xl p-7 lg:p-9 text-center shadow-[0_4px_24px_-8px_rgba(232,70,141,0.2)] hover:shadow-[0_14px_36px_-10px_rgba(232,70,141,0.32)] transition-shadow"
            >
              <span
                className="absolute top-4 right-4 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-white"
                style={{ background: "linear-gradient(135deg, #F7B2CD 0%, #E8468D 100%)" }}
              >
                Most Impact
              </span>

              <div className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ background: "linear-gradient(135deg, #F7B2CD 0%, #E8468D 100%)" }}>
                <Calendar className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <h3 className="font-extrabold text-2xl lg:text-3xl text-[#111827] mb-3">Give Monthly</h3>
              <p className="text-[15px] leading-relaxed text-[#4B5563] mb-7">
                Steady monthly support helps us plan ahead and keeps the portal running for every
                family who needs it. Choose your level. Cancel anytime.
              </p>
              <a
                href={MONTHLY_URL}
                {...externalProps}
                data-testid="donate-card-monthly-cta"
                className="btc-donate-btn inline-flex w-full items-center justify-center gap-2 px-6 py-3.5 rounded-full text-white font-semibold text-base shadow-[0_8px_20px_-6px_rgba(232,70,141,0.5)] hover:shadow-[0_12px_28px_-6px_rgba(232,70,141,0.65)] transition-all"
              >
                Give Monthly
                <ExternalLink className="w-4 h-4" />
              </a>
              <p className="mt-3 text-xs text-[#6B7280]">Opens in a new tab</p>
            </article>
          </div>
        </div>
      </section>

      {/* -------- CAMPAIGN: 5,000 STRONG (people, not money) -------- */}
      <section aria-label="5,000 Strong campaign" className="bg-white">
        <div className="max-w-[1180px] mx-auto px-5 sm:px-8 lg:px-12 pb-6 lg:pb-10">
          {/* Section 1: Campaign message */}
          <div className="text-center max-w-3xl mx-auto">
            <p
              className="text-xs sm:text-sm font-bold uppercase tracking-[0.22em] mb-4"
              style={{ color: "#D6316E" }}
            >
              5,000 STRONG
            </p>
            <h2 className="font-extrabold tracking-tight text-[#111827] text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mb-5">
              5,000 People. £5 a Month. One Promise.
            </h2>
            <p className="text-[16px] lg:text-[17px] leading-relaxed text-[#1F2937]">
              Every donation powers the Bonded Thru Cancer Portal, a free platform being built for
              anyone affected by cancer. No paywalls. No fees. Every service free forever for
              patients, survivors, caregivers, and families. Your £5 a month is what keeps it free.
            </p>
          </div>
        </div>
      </section>

      {/* Section 2: What Your £5 Builds */}
      <section aria-label="What your five pounds builds" className="bg-white">
        <div className="max-w-[1180px] mx-auto px-5 sm:px-8 lg:px-12 pb-14 lg:pb-16">
          <h3 className="text-center font-extrabold tracking-tight text-[#111827] text-2xl sm:text-3xl lg:text-4xl mb-8 lg:mb-10">
            What Your £5 Builds
          </h3>
          <ul
            data-testid="services-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5"
          >
            {CAMPAIGN_SERVICES.map((s) => (
              <li
                key={s.title}
                className="rounded-2xl p-5 lg:p-6 flex items-start gap-4 bg-white border border-[#F1D9E5] hover:border-[#F7B2CD] hover:shadow-[0_10px_30px_-14px_rgba(232,70,141,0.32)] transition-all"
              >
                <div
                  className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg, #FCE1EC 0%, #F7B2CD 100%)" }}
                  aria-hidden="true"
                >
                  <s.icon className="w-5 h-5 text-[#D6316E]" strokeWidth={2} />
                </div>
                <div>
                  <div className="font-bold text-[15px] lg:text-base text-[#111827] mb-1.5">
                    {s.title}
                  </div>
                  <p className="text-sm text-[#4B5563] leading-relaxed">{s.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* -------- WHY THIS MATTERS -------- */}
      <section className="bg-white">
        <div className="max-w-[1180px] mx-auto px-5 sm:px-8 lg:px-12 pb-14 lg:pb-20">
          <div
            className="rounded-3xl p-7 lg:p-10 flex flex-col md:flex-row gap-6 md:gap-8 items-start"
            style={{ background: "linear-gradient(180deg, #FFF3F7 0%, #FDE3EE 100%)" }}
          >
            <div
              className="w-14 h-14 rounded-full shrink-0 flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #F7B2CD 0%, #E8468D 100%)" }}
              aria-hidden="true"
            >
              <HandHeart className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <div>
              <h2 className="font-extrabold text-2xl sm:text-3xl lg:text-4xl text-[#111827] mb-4">
                Why This Matters
              </h2>
              <p className="text-[16px] lg:text-[17px] leading-relaxed text-[#1F2937] max-w-3xl">
                Cancer isolates. This community connects. Bonded Thru Cancer built something that did
                not exist before, a portal where patients, survivors, and families find each other
                and stand together. Your donation is what keeps it free, growing, and reaching the
                next family who needs it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* -------- TRUST SECTION -------- */}
      <section id="live-tracker" aria-label="Live donor tracker" className="bg-white">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 lg:px-12 pb-14 lg:pb-16">
          <div
            className="rounded-3xl p-6 sm:p-8 lg:p-10"
            style={{
              background: "linear-gradient(180deg, #FFF3F7 0%, #FDE3EE 100%)",
              border: "1px solid #F7B2CD",
            }}
          >
            <div className="text-center mb-6">
              <p className="text-[12px] sm:text-[13px] font-bold uppercase tracking-[0.22em] text-[#D6316E] mb-2">
                Live Tracker
              </p>
              <h3 className="font-extrabold text-[#111827] text-2xl sm:text-3xl lg:text-4xl leading-tight">
                <span data-testid="tracker-count">
                  <AnimatedCount value={progress.donors} />
                </span>{" "}
                of {progress.goal.toLocaleString("en-GB")} people have joined
              </h3>
            </div>

            {/* Progress bar */}
            <div
              className="relative w-full h-5 sm:h-6 rounded-full overflow-hidden bg-white/60 border border-[#F7B2CD]"
              role="progressbar"
              aria-valuenow={progress.donors}
              aria-valuemin={0}
              aria-valuemax={progress.goal}
              aria-label={`${progress.donors} of ${progress.goal} people have joined`}
              data-testid="tracker-progress"
            >
              <div
                className="absolute inset-y-0 left-0 rounded-full transition-all duration-[1200ms] ease-out"
                style={{
                  width: `${Math.max(2, pct)}%`,
                  background: "linear-gradient(90deg, #F17BAF 0%, #E8468D 60%, #D6316E 100%)",
                  boxShadow: "0 4px 16px -6px rgba(232, 70, 141, 0.45)",
                }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs sm:text-sm text-[#4B5563]">
              <span>{pct.toFixed(1)}% of goal</span>
              <span>Goal: {progress.goal.toLocaleString("en-GB")}</span>
            </div>

            {/* CTA */}
            <div className="mt-8 flex flex-col items-center gap-3">
              <p className="text-[15px] sm:text-base font-semibold text-[#111827]">
                Join them for £5 a month
              </p>
              <a
                href={MONTHLY_URL}
                {...externalProps}
                data-testid="tracker-cta-monthly"
                className="btc-donate-btn inline-flex w-full sm:w-auto items-center justify-center gap-2 px-8 py-4 rounded-full text-white font-semibold text-base shadow-[0_10px_30px_-8px_rgba(232,70,141,0.55)] hover:shadow-[0_14px_36px_-6px_rgba(232,70,141,0.7)] transition-all"
              >
                Give Monthly
                <ExternalLink className="w-4 h-4" />
              </a>
              <p className="text-xs text-[#6B7280]">Opens in a new tab. Cancel anytime.</p>
            </div>
          </div>
        </div>
      </section>

      {/* -------- TRUST CARDS -------- */}
      <section className="bg-white" aria-label="Trust and security">
        <div className="max-w-[1180px] mx-auto px-5 sm:px-8 lg:px-12 pb-14 lg:pb-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            <TrustCard
              icon={ShieldCheck}
              title="Charity"
              body={
                <>
                  Bonded Thru Cancer is a registered UK charity.
                  <br />
                  Number <strong>{CHARITY_NUMBER}</strong>.
                </>
              }
            />
            <TrustCard
              icon={Lock}
              title="Secure Payments"
              body="Payments are securely processed by Stripe. We never see or store your card details."
            />
            <TrustCard
              icon={Mail}
              title="Receipt"
              body="You will receive an email receipt for every donation."
            />
          </div>
        </div>
      </section>

      {/* -------- FINAL CTA -------- */}
      <section aria-label="Support Bonded Thru Cancer">
        <div
          className="text-white"
          style={{ background: "linear-gradient(135deg, #E8468D 0%, #D6316E 100%)" }}
        >
          <div className="max-w-[1180px] mx-auto px-5 sm:px-8 lg:px-12 py-10 lg:py-14 flex flex-col md:flex-row items-center gap-6 md:gap-8">
            <div
              className="w-14 h-14 rounded-full shrink-0 flex items-center justify-center bg-white/15 border border-white/30"
              aria-hidden="true"
            >
              <HandHeart className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="font-extrabold text-2xl sm:text-3xl lg:text-4xl leading-tight mb-2">
                Help Us Be There
              </h2>
              <p className="text-white/90 text-[15px] lg:text-base max-w-xl">
                Your support keeps this community free for every patient and family who needs it.
              </p>
            </div>
            <button
              type="button"
              onClick={scrollToTracker}
              data-testid="donate-final-cta"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white text-[#D6316E] font-semibold text-base shadow-[0_10px_28px_-8px_rgba(0,0,0,0.25)] hover:bg-[#FFF3F7] transition-colors"
              aria-label="Choose how to donate"
            >
              Donate
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* -------- FOOTER -------- */}
      <footer className="bg-white border-t border-[#F1D9E5]">
        <div className="max-w-[1180px] mx-auto px-5 sm:px-8 lg:px-12 py-10 lg:py-12 flex flex-col md:flex-row items-center md:items-start justify-between gap-6">
          <div className="flex flex-col items-center md:items-start gap-3">
            <img
              src={LOGO_URL}
              alt="Bonded Thru Cancer"
              className="h-16 md:h-20 w-auto"
              draggable={false}
            />
            <p className="text-xs text-[#4B5563] text-center md:text-left leading-relaxed">
              Registered UK Charity Number <strong>{CHARITY_NUMBER}</strong>
              <br />
              © {new Date().getFullYear()} Bonded Thru Cancer. All rights reserved.
            </p>
          </div>
          <a
            href="/"
            className="text-sm font-semibold text-[#D6316E] hover:text-[#B32660] transition-colors"
          >
            ← Back to bondedthrucancer.org
          </a>
        </div>
      </footer>

      <style>{`
        .btc-donate { background: #FFFFFF; }
        .btc-donate-btn {
          background: linear-gradient(135deg, #F17BAF 0%, #E8468D 60%, #D6316E 100%);
        }
        .btc-donate-btn:hover { filter: brightness(1.03); }
        .btc-donate-btn:active { transform: translateY(1px); }
        @media (prefers-reduced-motion: reduce) {
          .btc-donate * { transition-duration: 0s !important; animation: none !important; }
        }
      `}</style>
    </div>
  );
}

function MissionCard({ icon: Icon, title, body }) {
  return (
    <article
      className="rounded-3xl p-7 lg:p-9 flex flex-col md:flex-row gap-5 md:gap-6 items-start"
      style={{ background: "linear-gradient(180deg, #FFF3F7 0%, #FDE3EE 100%)" }}
    >
      <div
        className="w-14 h-14 rounded-full shrink-0 flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #F7B2CD 0%, #E8468D 100%)" }}
        aria-hidden="true"
      >
        <Icon className="w-6 h-6 text-white" strokeWidth={2} />
      </div>
      <div>
        <h3 className="font-extrabold text-xl sm:text-2xl text-[#111827] mb-2">{title}</h3>
        <p className="text-[15px] leading-relaxed text-[#374151]">{body}</p>
      </div>
    </article>
  );
}

function TrustCard({ icon: Icon, title, body }) {
  return (
    <div className="bg-[#FBFBFD] border border-[#EEF2F7] rounded-2xl p-5 lg:p-6 flex items-start gap-4">
      <div
        className="w-11 h-11 rounded-xl shrink-0 flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #FCE1EC 0%, #F7B2CD 100%)" }}
        aria-hidden="true"
      >
        <Icon className="w-5 h-5 text-[#D6316E]" strokeWidth={2} />
      </div>
      <div>
        <h3 className="font-bold text-[15px] text-[#111827] mb-1">{title}</h3>
        <p className="text-sm text-[#4B5563] leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
